import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, User, Star, Car, Ticket, AlertCircle, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const StaffCustomerDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerDetail();
  }, [id]);

  const fetchCustomerDetail = async () => {
    try {
      const response = await api.get(`/staff/customers/${id}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'gold': return 'badge-gold';
      case 'platinum': return 'badge-platinum';
      default: return 'badge-silver';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <StaffLayout title="Customer Details">
        <div className="space-y-6">
          <div className="h-48 rounded-xl animate-shimmer" />
          <div className="h-64 rounded-xl animate-shimmer" />
        </div>
      </StaffLayout>
    );
  }

  if (!data) {
    return (
      <StaffLayout title="Customer Details">
        <div className="text-center py-16">
          <p className="text-[#A0A0A0]">Customer not found</p>
          <Link to="/staff/customers" className="text-[#D4AF37] hover:underline mt-2 inline-block">
            Back to Customers
          </Link>
        </div>
      </StaffLayout>
    );
  }

  const { customer, loyalty, rides, vouchers, issues } = data;

  return (
    <StaffLayout title="Customer Details">
      <div className="space-y-6 animate-fade-in">
        {/* Back link */}
        <Link to="/staff/customers" className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-white transition-colors">
          <ArrowLeft size={18} /> Back to Customers
        </Link>

        {/* Customer Header */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8942D] flex items-center justify-center">
                <span className="text-[#0A0A0B] font-bold text-3xl">
                  {customer.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">{customer.name}</h2>
                  <span className={`badge ${getTierBadge(loyalty?.tier)}`}>
                    {loyalty?.tier || 'Silver'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-[#A0A0A0]">
                  <span className="flex items-center gap-2">
                    <Mail size={16} /> {customer.email}
                  </span>
                  {customer.phone && (
                    <span className="flex items-center gap-2">
                      <Phone size={16} /> {customer.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 md:gap-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#D4AF37]">{loyalty?.current_points?.toLocaleString() || 0}</p>
                  <p className="text-[#6B6B6B] text-sm">Current Miles</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">{loyalty?.lifetime_points?.toLocaleString() || 0}</p>
                  <p className="text-[#6B6B6B] text-sm">Lifetime Miles</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="rides">
          <TabsList className="bg-[#131316] border border-[#2A2A2F]">
            <TabsTrigger value="rides" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
              <Car size={16} className="mr-2" /> Rides ({rides?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="vouchers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
              <Ticket size={16} className="mr-2" /> Vouchers ({vouchers?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="issues" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
              <AlertCircle size={16} className="mr-2" /> Issues ({issues?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rides" className="mt-4">
            {rides?.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vehicle</th>
                      <th>Distance</th>
                      <th>Fare</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rides.map((ride) => (
                      <tr key={ride.id}>
                        <td className="text-white">{formatDate(ride.ride_date)}</td>
                        <td className="capitalize">{ride.vehicle_type}</td>
                        <td>{ride.distance_miles} mi</td>
                        <td>${(ride.fare_cents / 100).toFixed(2)}</td>
                        <td className="text-[#4ADE80] font-medium">+{ride.points_earned}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B6B6B]">
                <Car size={48} className="mx-auto mb-3 opacity-50" />
                <p>No rides recorded</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="vouchers" className="mt-4">
            {vouchers?.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Status</th>
                      <th>Issued</th>
                      <th>Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map((voucher) => (
                      <tr key={voucher.id}>
                        <td className="font-mono text-[#D4AF37]">{voucher.code}</td>
                        <td>
                          <span className={`badge ${
                            voucher.status === 'active' ? 'badge-success' :
                            voucher.status === 'used' ? 'badge-gold' : 'badge-error'
                          }`}>
                            {voucher.status}
                          </span>
                        </td>
                        <td>{formatDate(voucher.issued_at)}</td>
                        <td>{formatDate(voucher.expires_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B6B6B]">
                <Ticket size={48} className="mx-auto mb-3 opacity-50" />
                <p>No vouchers</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="issues" className="mt-4">
            {issues?.length > 0 ? (
              <div className="space-y-3">
                {issues.map((issue) => (
                  <Card key={issue.id} className="bg-[#0A0A0B] border-[#2A2A2F]">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{issue.subject}</p>
                          <p className="text-[#6B6B6B] text-sm mt-1">{issue.description}</p>
                          <p className="text-[#6B6B6B] text-xs mt-2">{formatDate(issue.created_at)}</p>
                        </div>
                        <span className={`badge ${
                          issue.status === 'open' ? 'badge-warning' :
                          issue.status === 'resolved' ? 'badge-success' : 'badge-gold'
                        }`}>
                          {issue.status}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B6B6B]">
                <AlertCircle size={48} className="mx-auto mb-3 opacity-50" />
                <p>No issues</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </StaffLayout>
  );
};

export default StaffCustomerDetail;
